import SectionCard from './SectionCard';
import ToggleField from './ToggleField';
import Segmented from '@/components/base/Segmented';
import Toggle from '@/components/base/Toggle';
import { type SalesSettings, type PrintFormat } from '@/lib/salesSettings';

interface SectionProps {
  settings: SalesSettings;
  patch: (partial: Partial<SalesSettings>) => void;
}

export function ImpresionSection({ settings, patch }: SectionProps) {
  return (
    <SectionCard
      icon="ri-printer-line"
      title="Impresión de venta"
      description="Configura la impresión de facturas. La conexión con impresoras se habilitará posteriormente."
    >
      <ToggleField
        label="¿Imprimir factura automáticamente después de vender?"
        checked={settings.auto_print_invoice}
        onChange={(v) => patch({ auto_print_invoice: v })}
      />

      <div>
        <p className="text-sm font-medium text-foreground-900 mb-2.5">Formato de impresión</p>
        <Segmented
          value={settings.print_format}
          onChange={(v) => patch({ print_format: v as PrintFormat })}
          options={[
            { value: '58mm', label: '58 mm' },
            { value: '80mm', label: '80 mm' },
            { value: 'A4', label: 'A4' },
          ]}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground-800 mb-1.5">
          Impresora predeterminada
        </label>
        <div className="flex items-center gap-3 rounded-md border border-background-300 bg-background-100 px-4 py-2.5">
          <i className="ri-printer-line text-foreground-500" />
          <span className="text-sm text-foreground-700">{settings.default_printer}</span>
          <span className="ml-auto inline-flex items-center gap-1 text-xs text-foreground-500">
            <i className="ri-lock-line" /> Próximamente
          </span>
        </div>
      </div>
    </SectionCard>
  );
}

const INVOICE_FIELDS: {
  key: keyof SalesSettings;
  label: string;
}[] = [
  { key: 'invoice_show_logo', label: 'Mostrar logo' },
  { key: 'invoice_show_address', label: 'Mostrar dirección' },
  { key: 'invoice_show_phone', label: 'Mostrar teléfono' },
  { key: 'invoice_show_rnc', label: 'Mostrar RNC' },
  { key: 'invoice_show_cashier', label: 'Mostrar cajero' },
  { key: 'invoice_show_datetime', label: 'Mostrar fecha y hora' },
  { key: 'invoice_show_payment_method', label: 'Mostrar método de pago' },
];

export function InfoFacturaSection({ settings, patch }: SectionProps) {
  return (
    <SectionCard
      icon="ri-receipt-line"
      title="Información de factura"
      description="Elige qué datos aparecerán en la factura impresa."
    >
      <div className="divide-y divide-background-100">
        {INVOICE_FIELDS.map((field) => (
          <div key={field.key} className="py-3 first:pt-0 last:pb-0 flex items-center justify-between gap-4">
            <span className="text-sm font-medium text-foreground-900">{field.label}</span>
            <Toggle
              checked={settings[field.key] as boolean}
              onChange={(v) => patch({ [field.key]: v } as Partial<SalesSettings>)}
              label={field.label}
            />
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

export function MensajeFinalSection({ settings, patch }: SectionProps) {
  return (
    <SectionCard
      icon="ri-chat-1-line"
      title="Mensaje al final de la factura"
      description="Texto que se mostrará al pie de cada factura."
    >
      <textarea
        value={settings.invoice_footer_message}
        onChange={(e) => patch({ invoice_footer_message: e.target.value })}
        maxLength={200}
        rows={3}
        className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition resize-none"
      />
      <p className="text-xs text-foreground-500">
        {settings.invoice_footer_message.length}/200 caracteres
      </p>
    </SectionCard>
  );
}